import React, { useEffect, useState } from 'react';
import api from '../../config/axios';
import { itemTotal } from '../../utils/tax';
import Header from '../common/Header';
import './ShiftManagement.css';
import { useNavigate } from 'react-router-dom';
import { useElapsed, formatElapsed } from '../../hooks/useElapsed';

interface Shift {
  _id: string;
  staffId: { username: string; email: string; role: string } | string;
  startTime: string;
  endTime?: string;
  status: string;
  totalOrders: number;
  totalRevenue: number;
}

interface ShiftManagementProps {
  user: any;
  onLogout: () => void;
}

const ShiftManagement: React.FC<ShiftManagementProps> = ({ user, onLogout }) => {
  const [activeShift, setActiveShift] = useState<Shift | null>(null);
  const [history, setHistory] = useState<Shift[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Đồng hồ đếm giờ làm cho ca đang mở.
  const elapsed = useElapsed(activeShift?.startTime);
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [selectedShift, setSelectedShift] = useState<any | null>(null);
  const [shiftDetail, setShiftDetail] = useState<any | null>(null);
  const navigate = useNavigate();

  const fetchActiveShift = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/shifts/active');
      setActiveShift(res.data.data);
    } catch (err) {
      setActiveShift(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/shifts/history');
      setHistory(res.data.data);
    } catch (err) {
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredHistory = selectedDate
    ? history.filter(shift => {
        const shiftDate = new Date(shift.startTime).toISOString().split('T')[0];
        return shiftDate === selectedDate;
      })
    : history;

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
  };

  const handleShowAll = () => {
    setSelectedDate('');
  };

  const handleShiftClick = async (shift: Shift) => {
    try {
      const res = await api.get(`/api/shifts/detail/${shift._id}`);
      setShiftDetail(res.data.data);
      setSelectedShift(shift);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Lỗi khi tải chi tiết ca làm');
    }
  };

  useEffect(() => {
    fetchActiveShift();
    fetchHistory();
  }, []);

  const handleStartShift = async () => {
    setError(''); setSuccess('');
    try {
      setLoading(true);
      await api.post('/api/shifts/start', {});
      fetchActiveShift();
      setSuccess('Bắt đầu ca thành công!');
      fetchHistory();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Lỗi khi bắt đầu ca');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseShift = async () => {
    setError(''); setSuccess('');
    try {
      setLoading(true);
      await api.post('/api/shifts/close', {});
      setActiveShift(null);
      setSuccess('Chốt ca thành công!');
      fetchHistory();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Lỗi khi chốt ca');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header user={user} onLogout={onLogout}>
        <span className="user-role">{user.role === 'admin' ? 'Quản lý' : 'Nhân viên'}</span>
      </Header>
      <div className="shift-container">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 18 }}>
          <button
            className="shift-back-btn"
            onClick={() => navigate('/')}
            title="Quay lại Dashboard"
          >
            ←
          </button>
          <div className="shift-title" style={{ flex: 1 }}>QUẢN LÝ CA LÀM</div>
        </div>
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        {loading && <div>Đang tải...</div>}
        {user.role !== 'admin' && (
          <div className="shift-action">
            {activeShift ? (
              <div className="shift-active-box">
                <div><b>Bắt đầu:</b> {new Date(activeShift.startTime).toLocaleString()}</div>
                <div><b>Trạng thái:</b> Đang làm việc</div>
                <div className="shift-elapsed">
                  <span className="shift-elapsed-label">Đã làm</span>
                  <span className="shift-elapsed-value">{elapsed}</span>
                </div>
                <button className="shift-close-btn" onClick={handleCloseShift}>Chốt ca</button>
              </div>
            ) : (
              <button className="shift-btn" onClick={handleStartShift}>Bắt đầu ca làm</button>
            )}
          </div>
        )}
        <div style={{ marginTop: 24}}>
          <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 8 }}>
            Lịch sử ca làm {user.role === 'admin' ? 'tất cả nhân viên' : 'của bạn'}
          </div>
          <div className="shift-history-filters">
            <input
              type="date"
              value={selectedDate}
              onChange={e => handleDateChange(e.target.value)}
              className="shift-history-date-input"
            />
            <button
              onClick={handleShowAll}
              className={`shift-history-filter-btn${selectedDate ? ' active' : ''}`}
            >
              {selectedDate ? 'Hiển thị tất cả' : 'Lọc theo ngày'}
            </button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="shift-table">
              <thead>
                <tr>
                  {user.role === 'admin' && <th>Nhân viên</th>}
                  <th>Bắt đầu</th>
                  <th>Kết thúc</th>
                  <th>Thời lượng</th>
                  <th>Trạng thái</th>
                  <th>Tổng đơn</th>
                  <th>Doanh thu</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.map(shift => (
                  <tr key={shift._id} onClick={() => handleShiftClick(shift)}>
                    {user.role === 'admin' && <td>{typeof shift.staffId === 'object' ? shift.staffId.username : shift.staffId}</td>}
                    <td>{new Date(shift.startTime).toLocaleString()}</td>
                    <td>{shift.endTime ? new Date(shift.endTime).toLocaleString() : '-'}</td>
                    <td className="shift-duration-cell">{formatElapsed(shift.startTime, shift.endTime)}</td>
                    <td>{shift.status === 'active' ? 'Đang làm' : 'Đã chốt'}</td>
                    <td>{shift.totalOrders}</td>
                    <td>{shift.totalRevenue.toLocaleString()} đ</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {selectedShift && shiftDetail && (
          <div className="shift-detail-modal">
            <div className="shift-detail-content">
              <h3>Chi tiết ca làm #{selectedShift._id.slice(-6).toUpperCase()}</h3><br/>
              <div className="shift-detail-info">
                <div><b>Nhân viên:</b> {typeof selectedShift.staffId === 'object' ? selectedShift.staffId.username : selectedShift.staffId}</div>
                <div><b>Bắt đầu:</b> {new Date(selectedShift.startTime).toLocaleString()}</div>
                <div><b>Kết thúc:</b> {selectedShift.endTime ? new Date(selectedShift.endTime).toLocaleString() : 'Chưa kết thúc'}</div>
                <div><b>Trạng thái:</b> {selectedShift.status === 'active' ? 'Đang làm' : 'Đã chốt'}</div>
                <div><b>Tổng đơn hàng:</b> {selectedShift.totalOrders}</div>
                <div><b>Doanh thu:</b> {selectedShift.totalRevenue.toLocaleString()} đ</div>
              </div>
              
              {shiftDetail.orders && shiftDetail.orders.length > 0 && (
                <div className="shift-orders-section">
                  <h4>Danh sách đơn hàng trong ca</h4>
                  <div className="shift-orders-list">
                    {shiftDetail.orders.map((order: any, idx: number) => (
                      <div key={order._id} className="shift-order-item">
                        <div className="shift-order-header">
                          <span><b>Đơn #{order._id.slice(-6).toUpperCase()}</b></span>
                          <span>
                            <b>Thời gian:</b> {(() => {
                              const d = new Date(order.createdAt);
                              const pad = (n: number) => n.toString().padStart(2, '0');
                              return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())} ${pad(d.getDate())}/${pad(d.getMonth()+1)}/${d.getFullYear()}`;
                            })()}
                          </span>
                          <span><b>Tổng tiền</b></span>
                        </div>
                        <div className="shift-order-items">
                          {order.items.map((item: any, itemIdx: number) => {
                            const lineTotal = itemTotal(item, order.taxRate || 0);
                            
                            return (
                              <div key={itemIdx} className="shift-order-item-detail">
                                <span>
                                  {item.imageUrl && (
                                    <img src={item.imageUrl} alt={item.name} style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 6, marginRight: 8, verticalAlign: 'middle' }} />
                                  )}
                                  <b>{item.name}</b>
                                </span>
                                <span><b>x{item.quantity}</b></span>
                                <span><b>{Math.round(lineTotal).toLocaleString()} đ</b></span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <button className="shift-detail-close-btn" onClick={() => { setSelectedShift(null); setShiftDetail(null); }}>
                Đóng
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ShiftManagement; 